import { useState, useCallback, useRef } from 'react';

type FinishType = 'init' | 'sending' | 'complete' | 'error';

const fetchPost = async <T> (api: string, state: T): Promise<{
  result: FinishType,
}> => {
  const method = 'POST';
  const body = JSON.stringify(state);
  const headers = {
    'Accept': 'application/json',
    'Content-Type': 'application/json',
  };

  return await fetch(api, { method, headers, body }).then((res) => res.json());
};

const useForm = <T> (init: T, api: string) => {
  const formRef = useRef<HTMLFormElement>(null);

  const [state, setState] = useState<T>(init);
  const [finish, setFinish] = useState<FinishType>('init');

  const handleInput: React.FormEventHandler<HTMLInputElement|HTMLTextAreaElement> = useCallback((e) => {
    const target = e.currentTarget;
    setState((prev) => {
      return {
        ...prev,
        [target.name]: target.value,
      };
    });
  }, []);

  const handleCheckbox: React.ChangeEventHandler<HTMLInputElement> = useCallback((e) => {
    const target = e.currentTarget;
    const value = Number(target.value);
    setState((prev) => {
      return prev[target.name].includes(value) ? {
          ...prev,
          [target.name]: prev[target.name].filter((v) => v !== value),
        } : {
          ...prev,
          [target.name]: [...prev[target.name], value],
        };
    });
  }, []);

  const handleRadio: React.ChangeEventHandler<HTMLInputElement> = useCallback((e) => {
    const target = e.currentTarget;
    const value = Number(target.value);
    setState((prev) => {
      return {
        ...prev,
        [target.name]: value,
      };
    });
  }, []);

  const handleSelect: React.ChangeEventHandler<HTMLSelectElement> = useCallback((e) => {
    const target = e.currentTarget;
    const value = target.value;
    setState((prev) => {
      return {
        ...prev,
        [target.name]: value,
      };
    });
  }, []);

  const handleSubmit: React.MouseEventHandler<HTMLButtonElement> = useCallback(async (e) => {
    e.preventDefault();
    setFinish('init');

    if (!formRef.current.reportValidity()) {
      return false;
    }

    document.body.style.pointerEvents = 'none';

    setFinish('sending');
    try {
      const response = await fetchPost(api, state);

      if (response.result === 'complete') {
        setState(init);
        setFinish('complete');
      } else {
        setFinish('error');
      }
    } catch (error) {
      setFinish('error');
      console.error(error);
    }

    document.body.style.pointerEvents = '';
  }, [state]);

  return [{ formRef, state, finish }, { setState, handleInput, handleCheckbox, handleRadio, handleSelect, handleSubmit }] as const;
};

export default useForm;